/**
 * Upload API Service
 *
 * Handles file uploads to S3-backed endpoints
 */

import type { AxiosResponse } from 'axios'
import { apiClient } from '../client'
import { API_ENDPOINTS } from '@/config/api'
import { handleApiError } from '@/utils/apiErrorHandling'

export interface UploadImageResponse {
  data?: {
    imageURL?: string
  }
  message?: string
  status?: string
}

const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to read file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

class UploadService {
  async uploadImage(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    try {
      onProgress?.(10)
      const dataUrl = await readFileAsDataUrl(file)
      const base64Payload = dataUrl.includes(',')
        ? `base64,${dataUrl.split(',')[1]}`
        : dataUrl

      const extFromName =
        file.name && file.name.includes('.')
          ? file.name.split('.').pop()?.toLowerCase()
          : undefined
      const extFromMime = file.type?.split('/')[1]
      const ext = extFromName || extFromMime || 'png'

      const payload = {
        mime: file.type,
        image: base64Payload,
        ext,
        name: file.name,
      }

      const response: AxiosResponse<UploadImageResponse> = await apiClient.post(
        API_ENDPOINTS.UPLOAD_IMAGE,
        payload
      )

      onProgress?.(100)

      const imageURL = response.data?.data?.imageURL
      if (!imageURL) {
        throw new Error('Upload succeeded but image URL was not returned')
      }

      return imageURL
    } catch (error) {
      throw handleApiError(error, 'uploadImage')
    }
  }
}

export const uploadService = new UploadService()
export default uploadService
